import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Drawer from "antd/es/drawer";
import Form from "antd/es/form";
import { useForm } from "antd/es/form/Form";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import Role from "@mapmycustomers/shared/enum/Role";
import { EntityTypesSupportingFieldCustomization } from "@mapmycustomers/shared/types/entity";
import FormLayoutRoleAssociation, {
  FormLayoutRoleAssociationRecord,
} from "@mapmycustomers/shared/types/layout/FormLayoutRoleAssociation";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import getDefaultDrawerContainer from "@app/util/ui/getPageContentContainer";
import useHiddenChat from "@app/util/useHiddenChat";

import {
  areLayoutsRolesAssociationsLoading,
  areLayoutsRolesAssociationsSaving,
  getActiveEntityType,
  getLayoutsRolesAssociationsList,
} from "../../store";
import { cancelLayoutRolesAssociation, saveLayoutsRoles } from "../../store/actions";
import DefaultAssociationFormValues from "../../type/DefaultAssociationFormValues";
import mapAssociationToRecord from "../../util/mapAssocationToRecord";
import TeamLayoutsGrid from "../TeamLayoutsGrid";

import styles from "./AssociationsSidebar.module.scss";

interface Props {
  analyticIssuer?: AnalyticsService;
  associations: Array<FormLayoutRoleAssociation>;
  entityType: EntityTypesSupportingFieldCustomization;
  hideForm: typeof cancelLayoutRolesAssociation;
  loading: boolean;
  saveLayoutsRoles: typeof saveLayoutsRoles.request;
  saving: boolean;
}

const messages = defineMessages({
  cancelBtn: {
    id: "settings.formLayouts.assignLayouts.footer.cancel",
    defaultMessage: "Cancel",
    description: "Forms Layouts - Assign Layouts pane - Cancel button title",
  },
  saveBtn: {
    id: "settings.formLayouts.assignLayouts.footer.save",
    defaultMessage: "Save Changes",
    description: "Forms Layouts - Assign Layouts pane - Save Changes button title",
  },
  text: {
    id: "settings.formLayouts.assignLayouts.confirm.text",
    defaultMessage: "Updated forms will apply immediately and may affect your team’s workflow.",
    description: "Form Layouts - Assign Layouts pane - confirmation text",
  },
  title: {
    id: "settings.formLayouts.assignLayouts.title",
    defaultMessage: "Assign Layouts",
    description: "Form Layouts - title of Assign Layouts pane",
  },
  understoodBtn: {
    id: "settings.formLayouts.assignLayouts.footer.understood",
    defaultMessage: "Understood",
    description: "Forms Layouts - Assign Layouts pane - Understood button title",
  },
  waitBtn: {
    id: "settings.formLayouts.assignLayouts.footer.wait",
    defaultMessage: "Wait",
    description: "Forms Layouts - Assign Layouts pane - Wait button title",
  },
});

const AssociationsSidebar: React.FC<Props> = ({
  associations,
  entityType,
  hideForm,
  loading,
  saveLayoutsRoles,
  saving,
}) => {
  const intl = useIntl();

  const [form] = useForm<DefaultAssociationFormValues>();
  const [isFormChanged, setFormChanged] = useState(false);

  const [popoverVisible, setPopoverVisible] = useState(false);

  const handleVisibleChange = useCallback((visible) => setPopoverVisible(visible), []);
  const handleCancel = useCallback(() => hideForm(), [hideForm]);

  const handleSubmit = useCallback(() => {
    setPopoverVisible(false);
    form.submit();
  }, [form]);

  const handleFinish = useCallback(() => {
    const value = form.getFieldValue("value");
    saveLayoutsRoles(value);
  }, [form, saveLayoutsRoles]);

  const filteredAssociations: Array<FormLayoutRoleAssociationRecord> = useMemo(
    () =>
      (associations ?? [])
        .filter((entry) => entry.role.key !== Role.OWNER && entry.layout.entity === entityType)
        .map(mapAssociationToRecord),
    [associations, entityType]
  );

  useEffect(() => {
    form.setFieldsValue({
      value: filteredAssociations,
    });
    setFormChanged(false);
  }, [filteredAssociations, form]);

  useHiddenChat();

  return (
    <Drawer
      className={styles.container}
      closable
      getContainer={getDefaultDrawerContainer()}
      mask={false}
      onClose={handleCancel}
      placement="right"
      title={intl.formatMessage(messages.title)}
      visible
      width="40rem"
    >
      <div className={styles.content}>
        <Form<DefaultAssociationFormValues>
          form={form}
          initialValues={{
            value: filteredAssociations,
          }}
          onFinish={handleFinish}
        >
          <TeamLayoutsGrid
            associations={filteredAssociations}
            form={form}
            onChange={setFormChanged}
          />

          <Form.Item hidden name="value">
            <div />
          </Form.Item>
        </Form>
      </div>

      <div className={styles.footer}>
        <Button onClick={handleCancel}>{intl.formatMessage(messages.cancelBtn)}</Button>

        <Popconfirm
          cancelText={intl.formatMessage(messages.waitBtn)}
          description={intl.formatMessage(messages.text)}
          disabled={!isFormChanged || loading || saving}
          okText={intl.formatMessage(messages.understoodBtn)}
          onConfirm={handleSubmit}
          onOpenChange={handleVisibleChange}
          open={popoverVisible}
          overlayClassName={styles.overlay}
          placement="topRight"
        >
          <Button
            className={cn({ [styles.submitActive]: popoverVisible })}
            disabled={!isFormChanged || loading}
            loading={saving}
            type="primary"
          >
            {intl.formatMessage(messages.saveBtn)}
          </Button>
        </Popconfirm>
      </div>
    </Drawer>
  );
};

const mapStateToProps = (state: RootState) => ({
  associations: getLayoutsRolesAssociationsList(state),
  entityType: getActiveEntityType(state),
  loading: areLayoutsRolesAssociationsLoading(state),
  saving: areLayoutsRolesAssociationsSaving(state),
});

const mapDispatchToProps = {
  hideForm: cancelLayoutRolesAssociation,
  saveLayoutsRoles: saveLayoutsRoles.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(AssociationsSidebar);
